import * as React from 'react';

import Input, { InputProps } from '../../atoms/Input';

const NUMBER_REGEX = /\D*(\d+|\d.*?\d)(?:,(\d{1,3}))?\D*$/;

export interface Props extends Omit<InputProps, 'type'> {
  value?: string;
  allowsDecimal?: boolean;
  decimalPrecision?: number;
}

const NumberInput: React.FunctionComponent<Props> = ({
  className,
  value,
  allowsDecimal = false,
  decimalPrecision = 0,
  onChange,
  onFocus,
  ...props
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  /**
   * Formats number with Intl
   */
  const formatNumber = React.useCallback(
    (textValue: number | '') => {
      if (!textValue && textValue !== 0) return '';
      if (Number.isNaN(textValue)) return '';

      return new Intl.NumberFormat('pt-BR', {
        style: 'decimal',
        minimumFractionDigits: decimalPrecision,
        maximumFractionDigits: decimalPrecision,
      }).format(textValue as number);
    },
    [decimalPrecision],
  );

  const [displayValue, setDisplayValue] = React.useState(
    formatNumber(value ? parseFloat(value) : ''),
  );

  /**
   * Formats currency and updates cursor position
   */
  const handleChange = React.useCallback<NonNullable<InputProps['onChange']>>(
    (e) => {
      const currencyParts = NUMBER_REGEX.exec(e.target.value);
      const rawValue = currencyParts
        ? parseFloat(
            `${currencyParts[1].replaceAll(/\D/g, '')}.${
              allowsDecimal ? currencyParts[2]?.slice(0, 2) || '00' : '00'
            }`,
          )
        : '';
      const formattedValue = formatNumber(rawValue);

      // Cursor
      const beforeCursor = e.target.value.slice(
        0,
        e.target.selectionStart || undefined,
      );
      const digitsBeforeCursor = beforeCursor.replace(/[^\d]/g, '').length;

      const nativeEvent = e.nativeEvent as InputEvent;

      // Updates display/onChange
      setDisplayValue(formattedValue);
      e.target.value = rawValue !== '' ? String(rawValue) : '';
      onChange?.(e);

      const justTypedComma =
        nativeEvent.inputType === 'insertText' && nativeEvent.data === ',';

      // Set Caret position
      window.requestAnimationFrame(() => {
        const inputEl = inputRef.current;
        if (!inputEl) return;

        let newCursor = 0;

        if (justTypedComma && formattedValue.includes(',')) {
          // Move cursor to the first digit after the comma
          newCursor = formattedValue.indexOf(',') + 1;
        } else {
          let digitCount = 0;
          let lastDigitPosition = 0;
          for (let i = 0; i < formattedValue.length; i += 1) {
            if (/\d/.test(formattedValue[i])) {
              digitCount += 1;
              lastDigitPosition = i;
            }
            if (digitCount === digitsBeforeCursor) {
              newCursor = i + 1;
              break;
            } else if (i === formattedValue.length - 1) {
              newCursor = lastDigitPosition + 1;
            }
          }
        }
        inputEl.setSelectionRange(newCursor, newCursor);
      });
    },
    [allowsDecimal, formatNumber, onChange],
  );

  // Keep state in sync with external value
  React.useEffect(() => {
    setDisplayValue(formatNumber(value ? parseFloat(value) : ''));
  }, [formatNumber, value]);

  return (
    <Input
      className={className}
      ref={inputRef}
      type="text"
      value={displayValue}
      onChange={handleChange}
      {...props}
    />
  );
};

export default NumberInput;
